import { onlineManager, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { BuildingIcon, CheckIcon, TrashIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { startOAuthConnect } from 'sdk';
import { appConfig, type EnabledOAuthProvider, isStrategyEnabled } from 'shared';
import { mapOAuthProviders } from '~/modules/auth/oauth-providers';
import { ssoEntryQueryOptions, ssoStartUrl } from '~/modules/auth/sso-providers';
import { withStepUp } from '~/modules/auth/step-up';
import { ConfirmText } from '~/modules/common/confirm-text';
import { useDialoger } from '~/modules/common/dialoger/use-dialoger';
import { HelpText } from '~/modules/common/help-text';
import { toaster } from '~/modules/common/toaster/toaster';
import { ToolCard } from '~/modules/common/tool-card';
import { ConnectedAppsList } from '~/modules/me/connected-apps-list';
import { DeleteSelf } from '~/modules/me/delete-self';
import { MfaSwitch } from '~/modules/me/mfa/switch';
import { PasskeysList } from '~/modules/me/passkeys/list';
import { meAuthQueryOptions } from '~/modules/me/query';
import { SessionsList } from '~/modules/me/sessions-list';
import { Totp } from '~/modules/me/totp';
import { Alert, AlertDescription } from '~/modules/ui/alert';
import { Badge } from '~/modules/ui/badge';
import { Button } from '~/modules/ui/button';
import { UpdateUserForm } from '~/modules/user/update-user-form';
import { useCurrentUser } from '~/modules/user/user-store';
import { cn } from '~/utils/cn';
import { tw } from '~/utils/tw';

const cardClass = tw('mx-auto sm:w-full');

export function AccountGeneralCard() {
  const user = useCurrentUser();

  return (
    <ToolCard label="c:general" unsaved id="update-user" className={cardClass}>
      <UpdateUserForm user={user} />
    </ToolCard>
  );
}

export function AccountSessionsCard() {
  const { t } = useTranslation();

  return (
    <ToolCard label="c:sessions" description={t('c:sessions.text')} className={cardClass}>
      <SessionsList />
    </ToolCard>
  );
}

export function AccountConnectedAppsCard() {
  const { t } = useTranslation();

  return (
    <ToolCard label="c:connected_apps" description={t('c:connected_apps.text')} className={cardClass}>
      <ConnectedAppsList />
    </ToolCard>
  );
}

export function AccountAuthenticationCard() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const { data: authData } = useSuspenseQuery(meAuthQueryOptions());
  const { enabledOAuth } = authData;

  // An SSO recovery link lands here naming the connection to connect. The user need not be a member of its
  // organization yet, so it is offered beside the institutions of their own organizations.
  const { connect } = useSearch({ from: '/_app/account' });
  const listed = authData.institutions.find((institution) => institution.connectionId === connect);
  const { data: entry } = useQuery({ ...ssoEntryQueryOptions(connect ?? ''), enabled: !!connect && !listed });
  const offered = entry?.status === 'active' ? { connectionId: entry.id, displayName: entry.institution.displayName, connected: false } : undefined;
  const institutions = offered ? [...authData.institutions, offered] : authData.institutions;
  const toConnect = [listed, offered].find((institution) => institution && !institution.connected);

  const [loadingProvider, setLoadingProvider] = useState<EnabledOAuthProvider | null>(null);
  const [connectingTo, setConnectingTo] = useState<string | null>(null);

  // The backend pins the provider's callback to this account first; the browser then leaves for the provider.
  const { mutate: connectProvider } = useMutation({
    mutationFn: async (_provider: EnabledOAuthProvider) => {
      await withStepUp(() => startOAuthConnect());
    },
    onMutate: (provider) => setLoadingProvider(provider),
    onSuccess: (_data, provider) => {
      const params = new URLSearchParams({ type: 'connect', redirectAfter: window.location.pathname + window.location.hash });
      window.location.assign(`${appConfig.backendAuthUrl}/${provider}?${params.toString()}`);
    },
    onError: () => setLoadingProvider(null),
  });

  const authenticateWithProvider = (provider: EnabledOAuthProvider) => {
    if (!onlineManager.isOnline()) return toaster.warning(t('c:action.offline.text'));
    connectProvider(provider);
  };

  // The same pin, then the institution's sign-in with `type=connect`: its callback links the identity to this account.
  const { mutate: connectInstitution } = useMutation({
    mutationFn: async (_connectionId: string) => {
      await withStepUp(() => startOAuthConnect());
    },
    onMutate: (connectionId) => setConnectingTo(connectionId),
    onSuccess: (_data, connectionId) => {
      window.location.assign(ssoStartUrl({ connectionId }, { type: 'connect', redirectAfter: window.location.pathname + window.location.hash }));
    },
    onError: () => setConnectingTo(null),
  });

  return (
    <ToolCard label="c:authentication" description={t('c:authentication.text')} className={cardClass}>
      <div className="text-sm">
        {
          /* MFA */
          isStrategyEnabled('passkey') && isStrategyEnabled('totp') && (
            <>
              <HelpText content={t('c:mfa.text')}>
                <div className="flex items-center">
                  <p className="font-semibold">{t('c:mfa')}</p>
                  {!user.mfaRequired && (
                    <Badge size="xs" variant="outline" className="ml-2 border-success text-success max-sm:hidden">
                      {t('c:recommended')}
                    </Badge>
                  )}
                </div>
              </HelpText>
              <MfaSwitch />
            </>
          )
        }
        {
          /* Passkeys */
          isStrategyEnabled('passkey') && (
            <>
              <HelpText content={t('c:passkey.text')}>
                <p className="font-semibold">{t('c:passkeys')}</p>
              </HelpText>
              <PasskeysList />
            </>
          )
        }

        {
          /* TOTP */
          isStrategyEnabled('totp') && (
            <>
              <HelpText content={t('c:totp.text')}>
                <p className="font-semibold">{t('c:totp')}</p>
              </HelpText>
              <Totp />
            </>
          )
        }

        {
          /* OAuth */
          isStrategyEnabled('oauth') && (
            <>
              <HelpText content={t('c:oauth.text')}>
                <p className="font-semibold">{t('c:oauth')}</p>
              </HelpText>

              <div className="mb-6 flex flex-col gap-3 sm:items-start">
                {appConfig.enabledOAuthProviders.map((id) => {
                  const provider = mapOAuthProviders.find((provider) => provider.id === id);
                  if (!provider) return null;
                  if (enabledOAuth.includes(id)) {
                    return (
                      <div key={provider.id} className="flex items-center justify-center gap-2 px-3 py-2">
                        <img
                          src={`/static/auth/${provider.id}-icon.svg`}
                          alt={provider.id}
                          className={cn('mr-2 size-4', provider.id === 'github' && 'dark:invert')}
                          loading="lazy"
                        />
                        <CheckIcon strokeWidth={3} className="size-4.5 text-success" />
                        {`${t('c:already_connected_to')} ${provider.name}`}
                      </div>
                    );
                  }
                  return (
                    // Assert is necessary because apps might not have all providers enabled
                    <Button
                      loading={loadingProvider === provider.id}
                      key={provider.id}
                      type="button"
                      variant="plain"
                      onClick={() => authenticateWithProvider(provider.id as EnabledOAuthProvider)}
                    >
                      <img
                        src={`/static/auth/${provider.id}-icon.svg`}
                        alt={provider.id}
                        className={cn('size-4', provider.id === 'github' && 'dark:invert')}
                        loading="lazy"
                      />
                      {`${t('c:add')} ${provider.name} ${t('c:account').toLowerCase()}`}
                    </Button>
                  );
                })}
              </div>
            </>
          )
        }

        {
          /* Institution accounts: the connections of the organizations the user belongs to */
          isStrategyEnabled('sso') && institutions.length > 0 && (
            <>
              <HelpText content={t('c:institution_accounts.text')}>
                <p className="font-semibold">{t('c:institution_accounts')}</p>
              </HelpText>

              {toConnect && (
                <Alert className="mb-3">
                  <AlertDescription>{t('c:connect_institution_prompt.text', { institution: toConnect.displayName })}</AlertDescription>
                </Alert>
              )}

              <div className="mb-6 flex flex-col gap-3 sm:items-start">
                {institutions.map((institution) =>
                  institution.connected ? (
                    <div key={institution.connectionId} className="flex items-center justify-center gap-2 px-3 py-2">
                      <BuildingIcon className="mr-2 size-4" />
                      <CheckIcon strokeWidth={3} className="size-4.5 text-success" />
                      {t('c:connected_to_institution', { institution: institution.displayName })}
                    </div>
                  ) : (
                    <Button
                      key={institution.connectionId}
                      type="button"
                      variant={institution.connectionId === toConnect?.connectionId ? 'default' : 'plain'}
                      loading={connectingTo === institution.connectionId}
                      onClick={() => {
                        if (!onlineManager.isOnline()) return toaster.warning(t('c:action.offline.text'));
                        connectInstitution(institution.connectionId);
                      }}
                    >
                      <BuildingIcon className="size-4" />
                      {t('c:connect_institution', { institution: institution.displayName })}
                    </Button>
                  ),
                )}
              </div>
            </>
          )
        }
      </div>
    </ToolCard>
  );
}

export function AccountDeleteCard() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const deleteButtonRef = useRef(null);

  const openDeleteDialog = () => {
    useDialoger.getState().create(<DeleteSelf dialog />, {
      id: 'delete-account',
      triggerRef: deleteButtonRef,
      className: 'md:max-w-xl',
      title: t('c:delete_account'),
      description: <ConfirmText i18nKey="c:confirm.delete_account" values={{ email: user.email, appName: appConfig.name }} />,
    });
  };

  return (
    <ToolCard label="c:delete_account" description={t('c:delete_account.text', { appName: appConfig.name })} className={cardClass}>
      <Button ref={deleteButtonRef} variant="destructive" className="w-full sm:w-auto" onClick={openDeleteDialog}>
        <TrashIcon />
        {t('c:delete_account')}
      </Button>
    </ToolCard>
  );
}
